import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, Receipt, Search } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { EmptyState, PageLoader } from '@/components/ui/States';
import { Table, TableWrapper, Td, Th, Tr } from '@/components/ui/Table';
import { useToast } from '@/components/ui/Toast';
import { useAuthStore } from '@/store/auth';
import { formatIdr, formatRelative } from '@/lib/format';

/**
 * Riwayat transaksi.
 *
 * Cakupannya berbeda menurut peran, dan pembedanya ditegakkan di SERVER,
 * bukan dengan menyembunyikan isian di layar ini. Batas yang cuma ada di
 * tampilan bisa dilewati dengan memanggil endpointnya langsung.
 *
 *   root  : seluruh riwayat, bisa disaring tanggal.
 *   admin : sepuluh hari terakhir, tanpa saringan tanggal.
 */

const WARNA = {
  paid: 'success',
  pending: 'warning',
  expired: 'neutral',
  failed: 'danger',
  refunded: 'neutral',
};

const NAMA_STATUS = {
  paid: 'Lunas',
  pending: 'Menunggu',
  expired: 'Kedaluwarsa',
  failed: 'Gagal',
  refunded: 'Dikembalikan',
};

const DialogTandaiLunas = ({ transaksi, onClose }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [catatan, setCatatan] = useState('');

  const tandai = useMutation({
    mutationFn: () => api.post(`/platform/transactions/${transaksi.id}/mark-paid`, { catatan }),
    onSuccess: () => {
      toast.success('Transaksi ditandai lunas', {
        description: 'Paket atau add-onnya langsung berlaku.',
      });
      queryClient.invalidateQueries({ queryKey: ['platform-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['platform-transactions-summary'] });
      onClose();
    },
    onError: (err) =>
      toast.error('Gagal menandai lunas', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Tandai lunas manual"
      description={`${transaksi.orderId} · ${formatIdr(transaksi.amountIdr)}`}
      size="sm"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Batal
          </Button>
          <Button
            loading={tandai.isPending}
            disabled={catatan.trim().length < 3}
            onClick={() => tandai.mutate()}
          >
            Tandai lunas
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-[var(--text-secondary)]">
          Dipakai untuk pembayaran di luar gerbang, misalnya transfer bank langsung. Paket atau
          add-onnya akan langsung berlaku begitu ditandai.
        </p>
        <Field
          label="Bukti pembayaran"
          required
          hint="Nomor referensi transfer, atau keterangan lain yang bisa ditelusuri nanti."
        >
          {(props) => (
            <Textarea
              {...props}
              rows={3}
              value={catatan}
              onChange={(e) => setCatatan(e.target.value)}
              placeholder="Transfer BCA 21/08 pukul 14.30, ref 8823194"
            />
          )}
        </Field>
        <p className="text-xs text-[var(--text-muted)]">
          Tindakan ini tercatat di audit log beserta nama Anda.
        </p>
      </div>
    </Modal>
  );
};

export const TransactionsAdminPage = () => {
  const { user } = useAuthStore();
  const adalahRoot = user?.platformRole === 'root';

  const [saringan, setSaringan] = useState({ dari: '', sampai: '', status: '' });
  const [tandaiTarget, setTandaiTarget] = useState(null);

  const kueri = Object.fromEntries(Object.entries(saringan).filter(([, v]) => v));

  const { data, isLoading } = useQuery({
    queryKey: ['platform-transactions', kueri],
    queryFn: () => api.get('/platform/transactions', { params: kueri }),
  });

  const { data: dataRingkas } = useQuery({
    queryKey: ['platform-transactions-summary'],
    queryFn: () => api.get('/platform/transactions/summary'),
  });

  if (isLoading) return <PageLoader label="Memuat transaksi..." />;

  const items = data?.data ?? [];
  const meta = data?.meta ?? {};
  const ringkas = dataRingkas?.data;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-[var(--text-primary)]">Transaksi</h1>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          {adalahRoot
            ? 'Seluruh riwayat pembayaran, bisa disaring menurut tanggal.'
            : 'Sepuluh hari terakhir. Riwayat yang lebih panjang hanya bisa dibuka akun root.'}
        </p>
      </div>

      {ringkas && (
        <div className="grid gap-4 sm:grid-cols-3">
          <Card className="p-5">
            <p className="text-xs text-[var(--text-muted)]">Uang masuk ({ringkas.cakupan})</p>
            <p className="mt-1 text-2xl font-extrabold text-[var(--text-primary)]">
              {formatIdr(ringkas.nilaiIdr)}
            </p>
          </Card>
          <Card className="p-5">
            <p className="text-xs text-[var(--text-muted)]">Transaksi lunas</p>
            <p className="mt-1 text-2xl font-extrabold text-[var(--text-primary)]">
              {ringkas.lunas}
            </p>
          </Card>
          <Card className="p-5">
            <p className="text-xs text-[var(--text-muted)]">Menunggu pembayaran</p>
            <p className="mt-1 text-2xl font-extrabold text-[var(--text-primary)]">
              {ringkas.perStatus?.pending ?? 0}
            </p>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader
          title="Riwayat"
          description={meta.cakupan ? `Cakupan: ${meta.cakupan} · ${meta.total ?? 0} transaksi` : undefined}
        />
        <CardBody className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            {meta.bisaSaringTanggal && (
              <>
                <Field label="Dari" className="w-44">
                  {(props) => (
                    <Input
                      {...props}
                      type="date"
                      value={saringan.dari}
                      onChange={(e) => setSaringan((s) => ({ ...s, dari: e.target.value }))}
                    />
                  )}
                </Field>
                <Field label="Sampai" className="w-44">
                  {(props) => (
                    <Input
                      {...props}
                      type="date"
                      value={saringan.sampai}
                      onChange={(e) => setSaringan((s) => ({ ...s, sampai: e.target.value }))}
                    />
                  )}
                </Field>
              </>
            )}
            <Field label="Status" className="w-52">
              {(props) => (
                <Select
                  {...props}
                  value={saringan.status}
                  onChange={(e) => setSaringan((s) => ({ ...s, status: e.target.value }))}
                >
                  <option value="">Semua status</option>
                  {Object.entries(NAMA_STATUS).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            {(saringan.dari || saringan.sampai || saringan.status) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSaringan({ dari: '', sampai: '', status: '' })}
              >
                Bersihkan
              </Button>
            )}
          </div>

          {items.length === 0 ? (
            <EmptyState
              compact
              icon={saringan.status || saringan.dari ? Search : Receipt}
              title="Tidak ada transaksi"
              description={
                saringan.status || saringan.dari
                  ? 'Tidak ada yang cocok dengan saringan ini.'
                  : 'Transaksi akan muncul di sini begitu ada yang membeli.'
              }
            />
          ) : (
            <TableWrapper>
              <Table>
                <thead>
                  <Tr>
                    <Th>Nomor pesanan</Th>
                    <Th>Organisasi</Th>
                    <Th>Isi</Th>
                    <Th>Nominal</Th>
                    <Th>Status</Th>
                    <Th>Waktu</Th>
                    {adalahRoot && <Th> </Th>}
                  </Tr>
                </thead>
                <tbody>
                  {items.map((t) => (
                    <Tr key={t.id}>
                      <Td className="font-mono text-xs">{t.orderId}</Td>
                      <Td>{t.organizationId?.name ?? '-'}</Td>
                      <Td className="max-w-64 truncate">
                        {t.items.map((i) => i.name).join(', ')}
                        {t.discount?.code && (
                          <Badge tone="success" size="sm" className="ml-1">
                            {t.discount.code}
                          </Badge>
                        )}
                      </Td>
                      <Td className="tabular font-semibold">{formatIdr(t.amountIdr)}</Td>
                      <Td>
                        <Badge tone={WARNA[t.status] ?? 'neutral'} size="sm">
                          {NAMA_STATUS[t.status] ?? t.status}
                        </Badge>
                      </Td>
                      <Td className="text-[var(--text-muted)]">{formatRelative(t.createdAt)}</Td>
                      {adalahRoot && (
                        <Td>
                          {t.status === 'pending' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={BadgeCheck}
                              onClick={() => setTandaiTarget(t)}
                            >
                              Tandai lunas
                            </Button>
                          )}
                        </Td>
                      )}
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          )}
        </CardBody>
      </Card>

      {tandaiTarget && (
        <DialogTandaiLunas transaksi={tandaiTarget} onClose={() => setTandaiTarget(null)} />
      )}
    </div>
  );
};
